import { useMemo } from 'react';
import type { ColumnData } from 'api';
import type { InitialChanges } from 'common/types/scalarSpreadsheet';
import {
  SpreadsheetConfig,
  type SpreadsheetConfigConstructorParams,
} from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import SpreadsheetNoteParser from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/SpreadsheetNoteParser';
import SpreadsheetNoteReverseParser, {
  PreviewNoteReverseParser,
} from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/SpreadsheetNoteReverseParser';
import { UseNoteSpreadsheetProps } from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseNoteSpreadsheet/useNoteSpreadsheet.props';
import {
  referenceColumnLegend,
  SpreadsheetNoteCellData,
  SpreadsheetNoteData,
  SpreadsheetNoteTableData,
} from 'components/SpreadsheetNotes/types';
import { ReferenceSpreadsheetConfig } from 'components/SpreadsheetNotes/utilities/ReferenceSpreadsheetConfig';
import {
  INTERNAL_REFERENCE_XLSX_ROW_HEIGHT_STYLE,
  ROW_REFERENCE_XLSX_ROW_HEIGHT_STYLE,
} from 'components/SpreadsheetNotes/utilities/xlsxLayout.utils';
import { useZoom } from 'context/ZoomContext';
import type { ICell, ParserParams, ReverseParserParams } from 'models/interfaces/spreadsheet-types/core-types';
import { getTableName } from 'pages/ValuationsAllocation/util/getApproachTableName/getApproachTableName';
import { alphabetGenerator } from 'utilities';

const SPREADSHEET_NOTE_ROWS = 50;
const SPREADSHEET_NOTE_COLUMNS = 10;

type SpreadsheetNoteRowConfig = ICell<
  SpreadsheetNoteCellData,
  SpreadsheetNoteData,
  ColumnData,
  SpreadsheetNoteTableData
>[];

type SpreadsheetNoteXlsxRowConfig = SpreadsheetNoteRowConfig[number] & {
  displayUndefined?: boolean;
  isXlsxPaddingRow?: boolean;
  isXlsxPreviewRow?: boolean;
};

type SpreadsheetNoteReverseParserFn = (
  params: ReverseParserParams<SpreadsheetNoteData, ColumnData, SpreadsheetNoteTableData, SpreadsheetNoteCellData>
) => SpreadsheetNoteData | void;

type SpreadsheetNoteParserParams = ParserParams<
  ColumnData,
  SpreadsheetNoteData,
  SpreadsheetNoteTableData,
  SpreadsheetNoteCellData
>;

type SpreadsheetNoteConfigParams = SpreadsheetConfigConstructorParams<
  SpreadsheetNoteData,
  ColumnData,
  SpreadsheetNoteTableData,
  SpreadsheetNoteCellData
>;

const useNoteSpreadsheet = ({
  notes,
  isAttributeMapperEnabled,
  attributeReferenceConfig,
  attributeManager,
  isPreview,
  documentId,
  displayOptions,
}: UseNoteSpreadsheetProps) => {
  const { zoomLevel } = useZoom();

  const spreadsheets = useMemo(() => {
    const columns = Array.from({ length: SPREADSHEET_NOTE_COLUMNS }, () => ({}));
    const rows = Array.from({ length: SPREADSHEET_NOTE_ROWS }, () => ({}));
    if (notes) {
      return notes.map(note => {
        //   adding an extra column and row help with merged cells
        const rowData = Object.values(note.content.row_data ?? {});
        const sheetRows = isPreview
          ? [
            ...rowData.map(row => ({ ...row, isXlsxPreviewRow: true })),
            { displayUndefined: true, isXlsxPaddingRow: true, isXlsxPreviewRow: true },
          ]
          : [...rowData, {}];
        const columnData = Object.values(note.content.column_data ?? {});
        const columnDataLength = columnData.length;
        const sheetColumns = [...columnData, {}];
        columnData.push({});
        let rowConfig = (rowData.length ? sheetRows : rows) as SpreadsheetNoteXlsxRowConfig[];
        const columnConfig = (columnDataLength ? sheetColumns : columns) as ColumnData[];
        const alphabet = alphabetGenerator([], columnConfig.length);

        if (attributeReferenceConfig && isAttributeMapperEnabled && isPreview) {
          const hasRowReferences = (rowNumber: number) =>
            attributeManager?.sheetAttributeMapping.sheetIndicatorMapping.some(
              item =>
                item.gridIndicators.rows[rowNumber] &&
                item.sheet === note.name &&
                item.documentReferenceId === documentId
            ) ?? false;

          rowConfig = rowConfig.map((row, rowIndex) => ({
            ...row,
            rowNumber: rowIndex + 1,
            ...(hasRowReferences(rowIndex + 1)
              ? {
                style: {
                  ...(row.style ?? {}),
                  ...ROW_REFERENCE_XLSX_ROW_HEIGHT_STYLE,
                },
              }
              : {}),
          }));
          alphabet.unshift(referenceColumnLegend);
          rowConfig.unshift({ rowNumber: 0, style: INTERNAL_REFERENCE_XLSX_ROW_HEIGHT_STYLE, isXlsxPreviewRow: true });
          columnConfig.unshift({});
        } else if (attributeReferenceConfig && isAttributeMapperEnabled) {
          alphabet.unshift(referenceColumnLegend);
          rowConfig.unshift({});
          columnConfig.unshift({});
        }

        let reverseParseFn = SpreadsheetNoteReverseParser as SpreadsheetNoteReverseParserFn;

        if (isPreview) {
          reverseParseFn = PreviewNoteReverseParser as SpreadsheetNoteReverseParserFn;
        }

        const name = note.name ? getTableName(note.name) : `Spreadsheet_Note${note.id || 0}`;
        const spreadsheetConfigParams = {
          tableData: {
            attributeReferenceConfig,
            isAttributeMapperEnabled,
            sheetName: note.name ?? undefined,
            documentId,
          },
          reverseParser: reverseParseFn,
          parser: (params: SpreadsheetNoteParserParams) =>
            SpreadsheetNoteParser(params, isPreview, attributeManager, zoomLevel),
          rowConfig,
          afterCellChanged: (changes: InitialChanges[]) => changes,
          reverseParseAfterLoad: true,
          columns: columnConfig,
          initialObj: note,
          alphabet,
          name,
          alwaysDisplayLegend: displayOptions?.showGridLegends ?? isPreview,
          showTitlesColumn: false,
          hasColTitle: false,
          ignoreCellExpressionErrors: true,
        };

        if (isAttributeMapperEnabled && attributeReferenceConfig) {
          return new ReferenceSpreadsheetConfig<
            SpreadsheetNoteData,
            ColumnData,
            SpreadsheetNoteTableData,
            SpreadsheetNoteCellData
          >(spreadsheetConfigParams as unknown as SpreadsheetNoteConfigParams);
        }

        return new SpreadsheetConfig<
          SpreadsheetNoteData,
          ColumnData,
          SpreadsheetNoteTableData,
          SpreadsheetNoteCellData
        >(spreadsheetConfigParams as unknown as SpreadsheetNoteConfigParams);
      });
    }
  }, [
    notes,
    isAttributeMapperEnabled,
    attributeReferenceConfig,
    attributeManager,
    isPreview,
    documentId,
    zoomLevel,
    displayOptions?.showGridLegends,
  ]);

  return { spreadsheets };
};

export default useNoteSpreadsheet;
